/**
 * MonetizationDialog — the ONE popup (P091 T0106/E10, PO 2026-09-20).
 *
 * "one centralized pop up like glowtest_FE repo" — this is that one. Any surface that wants the user
 * to pay, upgrade or add credits calls `useMonetization().open(reason)`; nothing else renders a paywall.
 *
 * Rendered from `plans.ts`, which is the stone ladder. This component contains NO numbers.
 *
 * Two labels that are not cosmetic (D-071/D-072):
 *   - what the plan gives is stated plain, OUTCOME first: "About 19.5 investor meetings a month,
 *     from 750 investor reachouts we write and send for you";
 *   - everything derived from the research bands is prefixed "est." — because it is an estimate,
 *     and the page says so out loud rather than in a footnote.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Modal, Tag } from "antd";
import { useNavigate } from "react-router";
import {
  CREDITS_OPTION,
  CREDITS_PER_USD,
  CREDIT_TOPUP_AMOUNTS_USD,
  CREDIT_TOPUP_DEFAULT_USD,
  PLANS,
  estCostPerMeeting,
  estMeetings,
  estReplies,
  formatCredits,
  usd,
  type Plan,
} from "./plans";
import { handToStripe, stripeCheckoutUrl } from "@/components/monetization/stripeNavigation";
import { startCreditsCheckout } from "@/services/api/monetizationApi";
import { posthogCapture } from "@/config/posthog";
import { useStripeLinks } from "./useStripeLinks";
import { getPayerIdentity } from "@/providers/firebase/authProvider";
import { rememberPostLoginPath } from "@/utils/postLoginPath";

export type MonetizationReason =
  | "out_of_credits"
  | "upgrade"
  | "buy_credits"
  | "plan"
  | "manual";

const REASON_COPY: Record<MonetizationReason, { title: string; lead: string }> = {
  out_of_credits: {
    title: "You have used this month's investor reachouts",
    lead: "Your outreach paused here rather than stopping quietly. Pick the month that matches your raise, and it continues from where it stopped.",
  },
  upgrade: {
    title: "Step up your raise",
    lead: "Every step up costs less per reachout — that is the whole ladder.",
  },
  buy_credits: {
    title: "Add credits",
    lead: "One-time, into the same balance. No subscription, nothing to cancel.",
  },
  plan: {
    title: "Choose your plan",
    lead: "What you buy is credits — the platform's currency, spendable on anything we sell. What we estimate you get is replies and meetings, and every estimate below is labelled.",
  },
  manual: {
    title: "Plans and credits",
    lead: "What you buy is credits — the platform's currency, spendable on anything we sell. What we estimate you get is replies and meetings, and every estimate below is labelled.",
  },
};

export interface MonetizationDialogProps {
  open: boolean;
  reason: MonetizationReason;
  onClose: () => void;
  /** Current credits in the wallet, when known. Shown honestly as a number, or not at all. */
  credits?: number | null;
  /** The plan the payer is already on, when known — so it is marked and cannot be bought twice. */
  currentTier?: string | null;
}

/** The largest plan's value for EACH estimate — every metric measured against its own full width, so
 *  the three cards compare on every row exactly as they compare on the reachout bar. */
const MAX_EST_REPLIES = Math.max(...PLANS.map(estReplies));
const MAX_EST_MEETINGS = Math.max(...PLANS.map(estMeetings));
const MAX_EST_COST_PER_MEETING = Math.max(...PLANS.map(estCostPerMeeting));

/** A value as a share of its metric's largest plan, with a 6% floor so the smallest is a mark and not
 *  a sliver — the same rule the reachout bar uses. */
const pct = (value: number, max: number): number =>
  max > 0 ? Math.max(6, Math.round((value / max) * 100)) : 100;

/** The accent fill of an outlined call-to-action, on HOVER and on KEYBOARD FOCUS together — one string,
 *  so a pointer user and a keyboard user are told the same thing. (Without the focus half, tabbing to a
 *  card would light its ring and leave its action looking unfilled, which reads as two different
 *  states.) */
const CTA_FILL =
  " group-hover:border-[#0AB3BF] group-hover:bg-[#0AB3BF] group-hover:text-white" +
  " group-focus-visible:border-[#0AB3BF] group-focus-visible:bg-[#0AB3BF] group-focus-visible:text-white";

/** ONE MEASUREMENT, IN THE SHAPE EVERY METRIC ON THE CARD WEARS — THE STANDARD.
 *
 *  PO 2026-09-21, after the gap analysis: *"you did '250 reachouts / month' on top of the bar and the
 *  other est. replies IN THE SAME horizontal layer. it should also be on TOP … use 250 reachouts / month
 *  as standard. the others should be the same … i like the number on the right side."* So:
 *
 *        label ...................................... value     <- one line, label left, number right
 *        ████████████████████████████████████████████████████    <- the bar, the card's FULL width
 *
 *  THE GAP THIS CLOSES. The card shipped TWO layouts for the same four facts: the reachout count had
 *  its label ABOVE a full-width bar with its number on the LEFT, while the three estimates had their
 *  labels BESIDE a bar that the label and the value shortened, with their numbers in a right-hand
 *  column. Two shapes, one card. Now there is ONE component, so a fifth metric cannot pick a third —
 *  and every bar runs the same width, so they are read against each other rather than against the
 *  length of their own label.
 *
 *  WHAT REMAINS DIFFERENT, AND ONLY THIS: the lead row (`size="lead"`) is set in the card's body size
 *  and the estimates in the label size, because the reachout count is what the plan IS while the rest
 *  are what we ESTIMATE from it. Structure identical, hierarchy deliberate.
 *
 *  THE COST WEARS A COLOUR OF ITS OWN, and the reason is not decoration ("make the cost in a different
 *  colour, cause they are judged differently"). Replies and meetings are read *more is better*; a cost
 *  is read the other way round, and the cheapest plan must not look like the worst one. The bar is the
 *  AMOUNT in both cases — longest on the dearest plan, shrinking as the plans get better value — and it
 *  is the NEUTRAL TONE that tells the reader this row is a different kind of quantity from the others.
 *  The theme's own grey, so no new colour enters the palette (D-094).
 */
function MetricRow({
  label,
  value,
  widthPct,
  size = "sub",
  isCost,
}: {
  label: string;
  value: string;
  widthPct: number;
  /** `lead` = the plan's own number; `sub` = a figure we estimate from it. */
  size?: "lead" | "sub";
  isCost?: boolean;
}) {
  const lead = size === "lead";
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span
          className={
            lead
              ? "text-sm font-medium text-foreground"
              : "text-label-xs text-slate-500 dark:text-slate-400"
          }
        >
          {label}
        </span>
        <span
          className={
            "shrink-0 tabular-nums " +
            (lead
              ? "text-sm font-semibold text-foreground"
              : "text-label-xs font-medium text-foreground")
          }
        >
          {value}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100 transition-colors group-hover:bg-slate-200 dark:bg-slate-800 dark:group-hover:bg-slate-700">
        <div
          className={
            "h-full rounded-full transition-shadow " +
            (isCost
              ? "bg-slate-500 dark:bg-slate-400"
              : "bg-[#0AB3BF] group-hover:shadow-[0_0_10px_rgba(10,179,191,0.65)]")
          }
          style={{ width: `${widthPct}%` }}
        />
      </div>
    </div>
  );
}

/** THE CARD ITSELF IS THE CONTROL, and both kinds of card wear it the same way.
 *
 *  PO 2026-09-21: "also the whole thing should be a button, no ? each of the 4 cards. make it WOW." So
 *  the click target is everything the customer is looking at while he decides, not a strip at the
 *  bottom of it. The cards' call-to-action is therefore drawn, not interactive: a <button> inside a
 *  pressable card is invalid markup and a second control for anyone on a keyboard, and the card already
 *  carries the ONE accessible control (role, tabIndex, Enter and Space all handled here, so no card can
 *  forget the keyboard).
 *
 *  THE HOVER MARKING (desktop): the card lifts, its border takes the product's own accent, the bars get
 *  a halo of it, and a teal glow lands underneath — the accent at 50% in a 32px shadow, so "WOW" stays
 *  the brand colour and no new one enters the palette (D-094). `motion-safe` keeps the lift for people
 *  who have not asked for reduced movement, and the focus ring is the same accent so a keyboard user
 *  sees exactly what a pointer user sees.
 *
 *  A card that cannot be pressed (the plan he is already on, or one whose checkout link is not minted)
 *  says so in its styling and gets NONE of it: an offer that cannot be taken must not behave like one.
 */
function CardShell({
  tone,
  interactive,
  onPress,
  ariaLabel,
  children,
}: {
  /** The border and background the card wears when nothing is happening to it. */
  tone: string;
  interactive: boolean;
  onPress: () => void;
  ariaLabel?: string;
  children: ReactNode;
}) {
  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? ariaLabel : undefined}
      aria-disabled={interactive ? undefined : true}
      onClick={interactive ? onPress : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              // A NESTED CONTROL KEEPS ITS OWN KEY. The top-up card holds real buttons (the amount
              // chips), and without this guard Enter on a $50 chip would also fire the card's press —
              // buying the amount he was still choosing.
              if (e.target !== e.currentTarget) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onPress();
              }
            }
          : undefined
      }
      className={
        "group flex flex-col rounded-xl border p-4 transition-all duration-200 motion-reduce:transition-none " +
        tone +
        (interactive
          ? " cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0AB3BF] focus-visible:ring-offset-2 motion-safe:hover:-translate-y-1 hover:border-[#0AB3BF] hover:bg-slate-50 hover:shadow-[0_12px_32px_-12px_rgba(10,179,191,0.5)] active:translate-y-0 active:shadow-sm dark:hover:bg-slate-900/40"
          : " cursor-default")
      }
    >
      {children}
    </div>
  );
}

function PlanCard({
  plan,
  url,
  onBuy,
  isCurrent,
  canBuy,
  maxReachouts,
}: {
  plan: Plan;
  url: string;
  onBuy: () => void;
  /** The plan this payer already pays for. Marked, and not sellable to him again. */
  isCurrent: boolean;
  /** We know who he is, so a payment can be attributed to his account. */
  canBuy: boolean;
  /** The largest plan's monthly reachouts — the bar's full width, so the three compare at a glance. */
  maxReachouts: number;
}) {
  const replies = estReplies(plan);
  const meetings = estMeetings(plan);
  const perMeeting = estCostPerMeeting(plan);
  // The bar is the whole visual argument: 250 / 750 / 1,700 reachouts as a SHAPE, so the three plans
  // can be compared without reading a number off each card. 6% is the floor, so the smallest plan is
  // still a visible mark rather than a sliver.
  const widthPct = maxReachouts > 0
    ? Math.max(6, Math.round((plan.reachoutsPerMonth / maxReachouts) * 100))
    : 100;

  // WHICH TWO THINGS THE BUTTON CAN SAY, and they are different facts (glowtest_FE marks the current
  // plan; the sign-in half is raisy's own addition — see `handleBuy`).
  const label = isCurrent
    ? `You are on ${plan.name}`
    : canBuy
      ? `Start ${plan.name}`
      : "Sign in, then choose";
  const disabled = isCurrent || !url;
  /** Whether this card can be PRESSED. Two of the four states are statements, not offers: the plan he
   *  already pays for, and a plan whose checkout link has not been minted yet. Everything else is
   *  pressable — including for a signed-out visitor, whose press is what sends him to sign in. */
  const pressable = !disabled;

  return (
    <CardShell
      // THE ACCENT IS NOT A RESTING STATE. PO 2026-09-21: "currently the 'Growth' has a turquoise colour.
      // the hovering should move this colour and leave the others grey." So all four cards REST GREY
      // and the accent arrives with the pointer — it marks the card you are about to press rather than
      // one that was chosen for you. `Most popular` still says what it says, in its tag; it no longer
      // paints the whole card, and no card's action is filled before it is pointed at either.
      tone={
        isCurrent
          ? "border-emerald-500 bg-emerald-50/40 dark:border-emerald-500/60 dark:bg-emerald-950/20"
          : "border-slate-200 dark:border-slate-700"
      }
      interactive={pressable}
      onPress={onBuy}
      ariaLabel={`${label} — ${usd(plan.monthlyPriceUsd)} a month`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3
          className={
            "text-base font-semibold transition-colors " +
            (pressable ? "group-hover:text-[#0AB3BF]" : "")
          }
        >
          {plan.name}
        </h3>
        {isCurrent ? (
          <Tag color="green">Your plan</Tag>
        ) : (
          plan.popular && <Tag color="#0AB3BF">Most popular</Tag>
        )}
      </div>
      <p className="mt-2 text-2xl font-semibold">
        {usd(plan.monthlyPriceUsd)}
        <span className="text-sm font-normal text-slate-500 dark:text-slate-400"> / month</span>
      </p>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{plan.tagline}</p>

      {/* THE FOUR MEASUREMENTS, ALL IN THE SAME SHAPE (see `MetricRow`): what the plan IS — its monthly
          reachouts — and then the three figures we ESTIMATE from it. What used to sit here was the
          "About N investor meetings a month, from M investor reachouts we write and send for you"
          sentence, repeated on all three cards AND again in the estimates AND again in the bullets
          below: three sayings of one fact is what made this popup long (PO: "too much text and not
          simplified"). */}
      <div className="mt-3 space-y-2">
        <MetricRow
          label="reachouts / month"
          value={plan.reachoutsPerMonth.toLocaleString("en-IE")}
          widthPct={widthPct}
          size="lead"
        />

        {/* The three we ESTIMATE — `est.` travels with each of them: they are the promise's softest
            half, and the only rows on the card that are not a commitment. */}
        <MetricRow
          label="est. replies"
          value={String(replies)}
          widthPct={pct(replies, MAX_EST_REPLIES)}
        />
        <MetricRow
          label="est. meetings"
          value={String(meetings)}
          widthPct={pct(meetings, MAX_EST_MEETINGS)}
        />
        <MetricRow
          label="est. cost / meeting"
          value={usd(perMeeting)}
          widthPct={pct(perMeeting, MAX_EST_COST_PER_MEETING)}
          isCost
        />
      </div>

      {/* THE TWO THINGS THAT MAKE THIS PLAN DIFFERENT, not five lines identical to the card beside it.
          `features[0]` is deliberately dropped: `definedPlan` derives it from the same numbers the
          card now shows directly, so it was the third telling of one fact. The last two are the ones
          that actually separate the tiers (the Evidence Trail, the Guarantee). */}
      <ul className="mt-3 flex-1 space-y-1 text-xs text-slate-600 dark:text-slate-400">
        {plan.features.slice(-2).map((f) => (
          <li key={f}>· {f}</li>
        ))}
      </ul>

      {/* THE ACTION, DRAWN RATHER THAN INTERACTIVE — the card around it is the button now. On hover
          (or focus) it fills with the accent, so the card says "press anywhere here" without a second
          control inside it. */}
      <span
        className={
          "mt-4 rounded-lg px-3 py-2 text-center text-sm font-medium transition " +
          (disabled
            ? "border border-dashed border-slate-300 text-slate-400 dark:border-slate-700"
            : "border border-slate-300 dark:border-slate-600" + CTA_FILL)
        }
      >
        {label}
      </span>
    </CardShell>
  );
}

/** THE ADD-CREDITS CARD — the amount is chosen HERE (PO 2026-09-21: *"you should have here a choose
 *  amount already, no ?"*).
 *
 *  It used to hand the buyer to Stripe to type the amount, because the Payment Link is
 *  pay-what-you-want and a Payment Link cannot be told an amount from outside it. The chooser moves the
 *  ONE decision that sets what he pays onto the card where he is already deciding, and what he picks is
 *  turned into a Checkout Session by our backend (`startCreditsCheckout`).
 *
 *  THE CREDITS SHOWN ARE AT THE GRANT'S OWN PEG (1 USD = 1,000 credits, the same arithmetic the backend
 *  returns and the webhook will grant), so the number on the card is the number that lands.
 *
 *  THE CHIPS ARE REAL CONTROLS and they stop their own press: picking $50 must not also buy $25. That
 *  restraint is what the card's key handler honours too — see `CardShell`.
 */
function CreditsCard({
  amountUsd,
  busy,
  interactive,
  label,
  notice,
  onPick,
  onPress,
}: {
  amountUsd: number;
  /** The session is being created — one press, one session, so the card says so instead of looking dead. */
  busy: boolean;
  interactive: boolean;
  label: string;
  /** What the last press could not do, in the backend's words when it has them. */
  notice?: string | null;
  onPick: (amountUsd: number) => void;
  onPress: () => void;
}) {
  const credits = amountUsd * CREDITS_PER_USD;
  return (
    <CardShell
      tone="border-slate-200 dark:border-slate-700"
      interactive={interactive}
      onPress={onPress}
      ariaLabel={`${label} — ${formatCredits(credits)} credits for $${amountUsd}`}
    >
      <h3
        className={
          "text-base font-semibold transition-colors " +
          (interactive ? "group-hover:text-[#0AB3BF]" : "")
        }
      >
        {CREDITS_OPTION.name}
      </h3>
      {/* WHAT HE BUYS, as the headline — the amount below it changes this number, so the card answers
          "how many credits" before it answers "how much". */}
      <p className="mt-2 text-2xl font-semibold">{formatCredits(credits)}</p>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        credits · {CREDITS_OPTION.priceLabel}
      </p>

      {/* THE CHOOSER. Four round amounts inside the backend's floor and ceiling, every one of them a
          round number of credits at the peg. */}
      <div className="mt-3 grid grid-cols-4 gap-1">
        {CREDIT_TOPUP_AMOUNTS_USD.map((a) => (
          <button
            key={a}
            type="button"
            aria-pressed={a === amountUsd}
            aria-label={`$${a} — ${formatCredits(a * CREDITS_PER_USD)} credits`}
            onClick={(e) => {
              e.stopPropagation();
              onPick(a);
            }}
            className={
              "rounded-md border px-1 py-1 text-label-xs font-medium transition " +
              (a === amountUsd
                ? "border-[#0AB3BF] bg-[#0AB3BF]/10 text-foreground"
                : "border-slate-300 text-slate-600 hover:border-slate-400 dark:border-slate-600 dark:text-slate-300")
            }
          >
            ${a}
          </button>
        ))}
      </div>

      <ul className="mt-3 flex-1 space-y-1 text-xs text-slate-600 dark:text-slate-400">
        {CREDITS_OPTION.features.slice(0, 2).map((f) => (
          <li key={f}>· {f}</li>
        ))}
      </ul>

      {notice && (
        <p className="mt-2 text-label-xs text-slate-500 dark:text-slate-400">{notice}</p>
      )}

      <span
        className={
          "mt-4 rounded-lg px-3 py-2 text-center text-sm font-medium transition " +
          "border border-slate-300 dark:border-slate-600" +
          CTA_FILL
        }
      >
        {busy ? "Opening checkout…" : label}
      </span>
    </CardShell>
  );
}

export function MonetizationDialog({
  open,
  reason,
  onClose,
  credits,
  currentTier,
}: MonetizationDialogProps) {
  const copy = REASON_COPY[reason];
  // The links come from the config kernel at runtime, with the build-time values as the fallback — so a
  // newly minted Stripe link goes live without a frontend rebuild (useStripeLinks explains why).
  const links = useStripeLinks();
  const navigate = useNavigate();
  // The PLAN links are the ones whose absence is worth announcing: the top-up no longer depends on a
  // minted link at all — our backend creates its session, for the amount chosen on the card.
  const anyLinkReady = PLANS.some((p) => Boolean(links[p.key]));
  /** The bar's full width — the biggest plan's month, so the other two are read against it. */
  const maxReachouts = Math.max(...PLANS.map((p) => p.reachoutsPerMonth));

  // WHO IS PAYING, read at the moment of the click (glowtest_FE gates its purchase on being signed in;
  // this is that gate, and the reason it is not optional is on the next line).
  const { uid } = getPayerIdentity();
  const canBuy = Boolean(uid);

  /** What the top-up card's action says — decided once, because the card IS the button and so this
   *  string is both what is drawn on it and its accessible name. */
  const creditsLabel = canBuy ? "Add credits" : "Sign in, then add credits";

  // THE AMOUNT, and the top-up's own two states. This is the one card whose press does not simply open a
  // URL: it asks the backend to create the Checkout Session first, so the call can be slow or can refuse
  // — and both are said ON the card rather than shown as a press that did nothing.
  const [topupUsd, setTopupUsd] = useState(CREDIT_TOPUP_DEFAULT_USD);
  const [topupBusy, setTopupBusy] = useState(false);
  const [topupNotice, setTopupNotice] = useState<string | null>(null);
  /** The plan rail's own notice — the sibling of `topupNotice`, for the same reason: a press that cannot
   *  open a checkout says why on the surface that was pressed. */
  const [planNotice, setPlanNotice] = useState<string | null>(null);

  /**
   * THE PURCHASE GATE — a payment we cannot attribute is a payment that will not become credits.
   *
   * This is not caution, it is the webhook's own behaviour, read out of the backend: `_grant_for_payment`
   * resolves the payer as `client_reference_id` -> the uid, falling back to the billing EMAIL, and when
   * neither names a claimed account it REFUSES to grant and defers the payment
   * (`no person entity claims email=… — grant deferred`). A signed-out buyer therefore pays, Stripe
   * takes the money, and his credits land nowhere he can see — the one outcome worse than a declined
   * purchase, because he cannot tell it happened.
   *
   * glowtest_FE prevents this by requiring sign-in before it opens a link; raisy's dialog did not, and
   * the hole was reachable from a logged-out browser. So: no identity, no checkout — we say so plainly,
   * remember where he was, and send him to sign in. The button carries the same message before he
   * clicks ("Sign in, then choose"), so the gate is announced rather than discovered.
   */
  const handleBuy = (url: string, tier?: string) => {
    setPlanNotice(null);
    if (!canBuy) {
      rememberPostLoginPath(window.location.pathname);
      onClose();
      navigate("/login");
      return;
    }
    // THE BUYER WAS SENT TO PAY (project091 T0108). Fired at the moment a checkout really opens, with
    // the rail and the tier, so "showed interest" and "paid" can be compared per plan rather than as
    // one number.
    if (!stripeCheckoutUrl(url)) {
      // NO LINK, NO SILENT NOTHING. `openStripe("")` returned without a word, so an unconfigured payment
      // link made this press do nothing at all — a control that looks clickable and does not, the class
      // RULES_FE_QA's critical walk forbids. The customer is told, and told that nothing was charged.
      setPlanNotice(
        "This plan's checkout link is not configured yet, so nothing opened and nothing was charged. " +
          "Tell us and we will send you the link directly.",
      );
      return;
    }
    // THE CAPTURE GOES BEFORE THE HANDOFF (the tab leaves for Stripe immediately after it).
    posthogCapture("checkout_open", { rail: "plan", tier: tier ?? "unknown" });
    // THE LINK IS ALREADY IN HAND, so the tab opens inside this click and is filled on the next tick —
    // the reference behaviour (glowtest_FE), never blockable, and the app stays where the customer was.
    void handToStripe(async () => url);
  };

  /**
   * THE TOP-UP'S PRESS: create the session for the amount he picked, then open it.
   *
   * The sign-in gate is the SAME gate as the plans', for the same reason (an unattributable payment is
   * refused by the webhook and deferred). What differs is the second step: there is no link to open until
   * the backend has made one, so a failure says so on the card — and never falls back to some other
   * payment page, whose amount would be a different decision from the one he just made.
   */
  const handleCreditsPress = async () => {
    if (!canBuy) {
      rememberPostLoginPath(window.location.pathname);
      onClose();
      navigate("/login");
      return;
    }
    setTopupNotice(null);
    setTopupBusy(true);
    // THE BACKEND'S OWN WORDS SURVIVE THE HANDOFF. They are captured here because the handoff owns the
    // request now (the tab has to open before we ask — see `handToStripe`), and "Choose between $5 and
    // $1000" is a refusal he can act on where ours would be a shrug.
    let refusal: string | null = null;
    try {
      const handoff = await handToStripe(async () => {
        const res = await startCreditsCheckout(topupUsd);
        const url = res?.data?.url ?? null;
        if (url) {
          // Only when a session URL really came back, so this is a checkout that opened — not a press
          // that tried. It fires before the tab is pointed at Stripe, and the app's own tab stays alive
          // in the normal case, so nothing races the unload.
          posthogCapture("checkout_open", {
            rail: "credits",
            amount_usd: topupUsd,
            credits: topupUsd * CREDITS_PER_USD,
          });
        } else {
          refusal = res?.message ?? null;
        }
        return url;
      });
      if (!handoff.ok) setTopupNotice(refusal || handoff.reason);
    } catch {
      setTopupNotice("Checkout didn't open. Press again.");
    } finally {
      setTopupBusy(false);
    }
  };

  // THE OFFER WAS SEEN (project091 T0108). Fired with the reason, so the funnel can be read per surface
  // (the send gate's refusal vs a deliberate "see the plans") instead of as one undifferentiated open.
  useEffect(() => {
    if (open) posthogCapture("paywall_open", { reason });
  }, [open, reason]);

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={920} title={copy.title}>
      <p className="text-sm text-slate-600 dark:text-slate-400">{copy.lead}</p>

      {typeof credits === "number" && (
        <p className="mt-2 text-sm">
          Credits in your wallet now: <strong>{formatCredits(credits)}</strong>
        </p>
      )}

      {/* The gate, said out loud and BEFORE the click — never a dead button with no reason, and now
          one sentence rather than three: the reason was worth stating once, not three ways. */}
      {!canBuy && (
        <p className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
          Sign in first — a payment we cannot match to your account cannot become credits.
        </p>
      )}

      {/* A PRESS THAT COULD NOT OPEN A CHECKOUT SAYS SO, on the page it happened on — the sibling of the
          top-up card's notice, and the end of the silent no-op that an unconfigured payment link produced. */}
      {planNotice && (
        <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
          {planNotice}
        </p>
      )}

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {PLANS.map((p) => (
          <PlanCard
            key={p.key}
            plan={p}
            url={links[p.key]}
            isCurrent={currentTier === p.key}
            canBuy={canBuy}
            maxReachouts={maxReachouts}
            onBuy={() => handleBuy(links[p.key], p.key)}
          />
        ))}

        {/* The Add-credits card lives INSIDE the same grid — one popup, not two (glowtest_FE shape).
            It carries no bar on purpose: a top-up has no quantity to compare, and a full-width bar next
            to three measured ones would read as "the biggest plan", which it is not. Unlike the three
            beside it, its AMOUNT is chosen on the card and turned into a Checkout Session by our
            backend — so it is always pressable, a signed-out visitor's press being what sends him to
            sign in. */}
        <CreditsCard
          amountUsd={topupUsd}
          busy={topupBusy}
          interactive
          label={creditsLabel}
          notice={topupNotice}
          onPick={(a) => {
            setTopupUsd(a);
            // A new amount answers whatever the last press could not do.
            setTopupNotice(null);
          }}
          onPress={() => void handleCreditsPress()}
        />
      </div>

      {/* THE FOOTNOTE, CUT TO WHAT IT HAS TO SAY (was ~8 lines). Two obligations live here and
          neither is decoration: the per-reachout price must be stated somewhere, and the estimates
          must be labelled as estimates. Everything else in it — that credits are a currency, that
          they spend platform-wide, that cancellation runs through Stripe — is already said on the
          cards, in the lead, or in the plan kernel the cards are built from. */}
      <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
        <strong>Per reachout, not per meeting</strong> — one costs{" "}
        {PLANS.map((p) => formatCredits(p.creditsPerReachout)).join(" / ")} credits as the plan rises,
        spendable on anything here. Figures marked <strong>est.</strong> come from published
        cold-outreach research (6.5% reply, 40% of replies convert); we guarantee a response, not a
        meeting. Month-to-month, via Stripe.
      </p>

      {!anyLinkReady && (
        <p className="mt-2 text-xs text-[#0AB3BF]">
          Checkout links are being set up. Nothing here charges you yet.
        </p>
      )}
    </Modal>
  );
}
